import clsx from 'clsx';
import React, { FC, forwardRef, HTMLProps, PropsWithChildren } from 'react';
import { Stack, StackProps } from '../../stack';
import classes from './card-footer.module.scss';

export type CardFooterProps = PropsWithChildren & StackProps &
{
  /**
   * Render divider at the top of the component
   */
  divider?: boolean;
} & Omit<HTMLProps<HTMLDivElement>, 'ref'>;

/**
 * A functional component that serves as the footer of a card. This component accepts custom children and supports additional styling options, including a divider.
 *
 * The `Footer` component can be used to define the bottom section of a card layout. It provides the ability to align its content and optionally display a dividing line above the footer content.
 *
 * Properties:
 * - `children` - Content to be rendered inside the footer, such as buttons or other components.
 * - `divider` - A boolean flag indicating whether a divider line should be displayed above the footer.
 * - `...props` - Additional props passed to the underlying `<div>` element, allowing customization of the component's behavior or appearance.
 *
 * Forward Reference:
 * The `Footer` component supports forwarding a reference to the root div element, allowing direct DOM access if needed.
 *
 * Styling:
 * - Configurable through class names for root styling and optional divider rendering.
 * - Built-in alignment via `Stack`, ensuring content is properly aligned to the end and centered crosswise.
 */
export const Footer = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ children, divider, ...props }, ref) => {
    return (
      <Stack
        direction="column"
        crossAxis="stretch"
        align={'stretch'}
        {...props}
        ref={ref}
        className={clsx('card-footer-component', classes.root, props.className, {
          [classes.withDivider]: divider
        })}
      >
        {children}
      </Stack>
    );
  }
);

export const FooterContent: FC<PropsWithChildren & StackProps> = ({ children, ...props }) => {
  return <Stack fill align={'end'} crossAxis={'center'} spacing={2} {...props}
    className={clsx(classes.footerContent, props.className, 'card-footer-content-component')}>{children}</Stack>;
};

Footer.displayName = 'Card.Footer';